import "common/form/deprecated_form.scss";

import { useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { useForm } from "common/core/form";
import { usePermissions } from "common/core/current_user_role";
import Button from "common/core/button";
import { EmailTextInput } from "common/core/form/text";
import { isAriaInvalid } from "common/core/form/error";
import type { BillingSettings_node_Organization as Organization } from "common/settingsv2/sidebar_settings/billing/billing_query.graphql";
import { useMutation } from "util/graphql";
import { MutationErrorModal } from "common/settingsv2/modals/mutation_error_modal";
import { Card, CardHeading, CardSection, CardText } from "common/core/card";
import { pushNotification } from "common/core/notification_center/actions";
import { NOTIFICATION_SUBTYPES, NOTIFICATION_TYPES } from "constants/notifications";

import Styles from "./contact.module.scss";
import UpdateOrganizationBillingEmailMutation from "./update_organization_billing_email_mutation.graphql";

const MESSAGES = defineMessages({
  billingContactSuccess: {
    id: "4891a197-6609-4537-a543-dfa196684153",
    defaultMessage: "Billing email successfully updated",
  },
  submitError: {
    id: "d7c5d21e-39b1-414a-baa7-9b3232288ea3",
    defaultMessage:
      "There was an error updating the billing contact info. Please try again or contact support.",
  },
});

type FormValues = {
  billingContact: string | null;
};

export function ContactDetails({ organization }: { organization: Organization }) {
  const intl = useIntl();
  const { hasPermissionFor } = usePermissions();
  const updateOrganizationBillingEmail = useMutation(UpdateOrganizationBillingEmailMutation);

  const [status, setStatus] = useState<"error" | "success" | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors, isSubmitting },
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      billingContact: organization.billingEmail || null,
    },
  });

  const id = organization.id;
  const formValues = watch();
  const isBillingContactSpecified = formValues.billingContact !== null;

  const onSubmit = async (values: FormValues) => {
    try {
      await updateOrganizationBillingEmail({
        variables: { input: { id, billingEmail: values.billingContact! } },
      });
      setStatus("success");
      pushNotification({
        type: NOTIFICATION_TYPES.DEFAULT,
        message: intl.formatMessage(MESSAGES.billingContactSuccess),
      });
    } catch {
      pushNotification({
        type: NOTIFICATION_TYPES.DEFAULT,
        subtype: NOTIFICATION_SUBTYPES.ERROR,
        position: "topCenter",
        message: intl.formatMessage(MESSAGES.submitError),
      });
    }

    if (!hasPermissionFor("editOrganizationDetails")) {
      return null;
    }
  };

  return (
    <form data-automation-id="contact-form" onSubmit={handleSubmit(onSubmit)}>
      <Card
        loading={isSubmitting}
        footer={
          <>
            <Button
              isLoading={isSubmitting}
              automationId="update-billing-email-button"
              type="submit"
              buttonColor="action"
              variant="primary"
              disabled={!isValid}
            >
              <FormattedMessage
                id="5d4a474e-07b0-4d0c-9f07-79ca5d699e5f"
                defaultMessage="{isBillingContactSpecified, select, true{Update} other{Set}} billing email"
                values={{ isBillingContactSpecified }}
              />
            </Button>
          </>
        }
      >
        <CardSection>
          <CardHeading level="h3">
            <label htmlFor="billingContact">
              <FormattedMessage
                id="910125c4-6db9-40e0-bf84-729b0529b26c"
                defaultMessage="Billing Email"
              />
            </label>
          </CardHeading>
          <CardText>
            <p className={Styles.info}>
              <FormattedMessage
                id="c3438686-ef9b-4f1c-9a0d-f36a766ef87c"
                defaultMessage="Billing email is used for receipts and invoices."
              />
            </p>
          </CardText>
          <div className={Styles.inputContainer}>
            <EmailTextInput
              id="billingContact"
              data-automation-id="billing-contact-field"
              aria-invalid={isAriaInvalid(errors.billingContact)}
              {...register("billingContact")}
            />
          </div>
        </CardSection>
        {status === "error" && <MutationErrorModal onClick={() => setStatus(null)} />}
      </Card>
    </form>
  );
}
